import { useApolloClient } from '@apollo/client';
import {
  usePendingSourceCreateSourceMutation,
  usePendingSourceQuery,
  usePendingSourceUpdateMutation,
} from '@mnd-apps/publish/generated/hooks';
import { track } from '@mnd-apps/publish/lib/tracker';
import routes from '@mnd-apps/publish/routes';
import IconArrowRight from '@mnd-frontend/icons/components/ArrowRight';
import { Button, Text } from '@mnd-frontend/ui/publish/components';
import SmartLoaderAnimation from '@mnd-frontend/ui/publish/components/SmartLoader';
import { media } from '@mnd-frontend/ui/publish/components/utils';
import { useNavigate, useToasts } from '@mnd-frontend/ui/publish/contexts';
import {
  GREY_800,
  GREY_1000,
  RED_200,
  RED_700,
  SIZING_1,
  SPACING_1,
  SPACING_2,
  SPACING_3,
  SPACING_6,
} from '@mnd-frontend/ui/shared/tokens';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css, keyframes } from 'styled-components';
import { type StepsMapType, useOnboardingLocaleText } from './hooks/useOnboardingLocaleText';
import StepBrand from './steps/StepBrand';
import StepCompetitors from './steps/StepCompetitors';
import StepNavigatorScreen from './steps/StepNavigatorScreen';
import { STEP, type Steps } from './types';

// #region Styled components
const Wrapper = styled.div`
  background: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  flex-direction: column;
  padding-left: ${SPACING_2};
  padding-right: ${SPACING_2};
`;

const Content = styled.div`
  max-width: 45rem;
  width: 100%;
`;

const Disclaimer = styled.p`
  font-size: 0.875rem;
  line-height: 1.3125rem;
  color: ${GREY_800};
  font-style: italic;
  margin-top: 1rem;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 2rem 0;
  gap: ${SPACING_1};
`;

const introHeadingAnimation = keyframes`
  0%   { font-size: 2.5rem; }
  70%  { font-size: 2.5rem; }
  100% { font-size: 1.5rem; }
`;

const opacityAnimation = keyframes`
  0%   { opacity: 0; }
  80%  { opacity: 0; }
  100% { opacity: 1; }
`;

const IntroHeading = styled.h1`
  text-align: center;
  font-size: 2.5rem;
  font-weight: 400;
  color: ${GREY_1000};
  animation: ${introHeadingAnimation} 1.5s ease forwards;
  margin: 0;
`;

const IntroSubHeading = styled.h2`
  text-align: center;
  font-size: 2rem;
  font-weight: 700;
  line-height: 2.5rem;
  margin: 0;
  opacity: 0;
  color: ${GREY_1000};
  animation: ${opacityAnimation} 1.5s ease forwards;
`;

const ButtonRow = styled.div<{ $isIntro?: boolean }>`
  display: flex;
  justify-content: ${({ $isIntro }) => ($isIntro ? 'center' : 'space-between')};
  flex-direction: column;
  gap: ${SPACING_1};
  margin-top: ${SPACING_3};

  ${media.greaterThan('tablet')} {
    flex-direction: row-reverse;
  }

  ${({ $isIntro }) =>
    $isIntro &&
    css`
    button {
      opacity: 0;
      animation: ${opacityAnimation} 1.5s ease forwards;
    }
  `}
`;

const NestedButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING_1};

  ${media.greaterThan('tablet')} {
    flex-direction: row-reverse;
    gap: ${SPACING_3};
  }
`;
// #endregion

// #region Loader & Stepper
const Steps = styled.div<{ $isIntro?: boolean }>`
  display: flex;
  justify-content: center;
  margin-top: ${SPACING_6};
  gap: ${SPACING_1};

  ${({ $isIntro }) =>
    $isIntro &&
    css`
      opacity: 0;
      animation: ${opacityAnimation} 1.5s ease forwards;
  `}
`;

const AlignSteppers = styled.div`
    display: inline-flex;
    gap: ${SIZING_1};
`;

const Dot = styled.div<{ $isActive: boolean }>`
    width: ${SIZING_1};
    height: ${SIZING_1};
    border-radius: 50%;
    background-color: ${props => (props.$isActive ? RED_700 : RED_200)};
`;

const Stepper = ({
  numOfSteps,
  activeStepIndex,
}: {
  numOfSteps: number;
  activeStepIndex: number;
}) => (
  <AlignSteppers>
    {Array.from({ length: numOfSteps }).map((_, index) => (
      <Dot key={index} $isActive={activeStepIndex === index} />
    ))}
  </AlignSteppers>
);

const LoaderWrapper = styled.div`
  display: flex;
  text-align: center;
  flex-direction: column;
  align-items: center;
  gap: ${SPACING_1};
`;

const SmartLoaderHeader = styled.header`
  margin-bottom: ${SPACING_2};
`;

const SmartLoader = ({
  currentStepLocaleText,
}: {
  currentStepLocaleText?: StepsMapType[Steps];
}) => {
  return (
    <LoaderWrapper>
      <SmartLoaderAnimation />
      {currentStepLocaleText && 'loader' in currentStepLocaleText && (
        <>
          <SmartLoaderHeader>
            <Text as="h1" variant="headlineMedium" color="GREY_1000">
              {currentStepLocaleText.loader?.headline}
            </Text>
          </SmartLoaderHeader>
          <Text as="p" variant="bodyLarge" color="GREY_1000">
            {currentStepLocaleText.loader?.note1}
          </Text>
          <Text as="p" variant="bodyLarge" color="GREY_800">
            {currentStepLocaleText.loader?.note2}
          </Text>
        </>
      )}
    </LoaderWrapper>
  );
};
// #endregion

// #region Component
const Onboarding = () => {
  const { pendingSourceId } = routes.onboarding.useParams();
  const { showFailureToast } = useToasts();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [navigateToPressroomId, setNavigateToPressroomId] = useState<string | undefined>(undefined);

  const [selectedBrandIds, setSelectedBrandIds] = useState<string[]>([]);

  // For the whole flow
  const { data, startPolling, stopPolling, loading } = usePendingSourceQuery({
    variables: {
      id: pendingSourceId,
    },
  });

  const apolloClient = useApolloClient();

  // Step towards complete
  const [createPressroom, { loading: creatingPressroom }] = usePendingSourceCreateSourceMutation({
    variables: {
      id: pendingSourceId,
    },
    onCompleted: newData => {
      const error = newData.pendingSourceCreateSource?.error;
      if (error) {
        setStep(STEP.COMPETITORS);
        showFailureToast(error.message);
      } else {
        const pressroomId = newData.pendingSourceCreateSource?.pressroom?.id;
        if (!pressroomId) {
          navigate(routes.pressroomSelector());
        }
        setNavigateToPressroomId(pressroomId);
        apolloClient.cache.evict({
          id: apolloClient.cache.identify({
            __typename: 'PendingSource',
            id: pendingSourceId,
          }),
        });
      }
    },
    onError: () => showFailureToast(t('onboarding_trial_error_network')),
  });

  // Step towards complete
  const [sendCompetitorsAndSetPressroom, { loading: sendCompetitorsLoading }] =
    usePendingSourceUpdateMutation({
      variables: {
        input: {
          id: pendingSourceId,
          activeCompetitorIds: selectedBrandIds,
        },
      },
      onCompleted: newData => {
        const error = newData.pendingSourceUpdate?.error;
        if (error) {
          if (error.__typename !== 'PendingSourceUpdateValidationError') {
            showFailureToast(error.message);
            setStep(STEP.COMPETITORS);
          }
        } else {
          createPressroom();
        }
      },
      onError: () => {
        setStep(STEP.COMPETITORS);
        showFailureToast(t('onboarding_trial_error_network'));
      },
    });

  const [step, setStep] = useState<Steps>(STEP.INTRO);

  // For tracking
  const trackBrandNameChange = useRef(false);
  const trackBrandUrlChange = useRef(false);
  const trackCompetitorChange = useRef(new Set<string>([]));

  const pendingSource = data?.me?.pendingSource;
  const pendingCompetitors = !!data?.me?.pendingSource?.pendingCompetitors;
  const pendingBrand = !!data?.me?.pendingSource?.pendingBrand;
  const shouldPoll = pendingBrand || pendingCompetitors;

  useEffect(() => {
    if (shouldPoll) {
      startPolling(1000);
    } else {
      stopPolling();
    }
  }, [shouldPoll, startPolling, stopPolling]);

  useEffect(() => {
    if (!loading && !pendingSource && !navigateToPressroomId) {
      navigate(routes.pressroomSelector());
    }
  }, [navigate, loading, pendingSource, navigateToPressroomId]);

  const currentStepLocaleText = useOnboardingLocaleText()[step];

  const showFullPageLoader = () => {
    if (step === STEP.INTRO) return false;

    if (step === STEP.BRAND) {
      return pendingBrand;
    }

    if (step === STEP.COMPLETE) {
      return true;
    }

    return pendingCompetitors || creatingPressroom || sendCompetitorsLoading;
  };

  if (navigateToPressroomId) {
    return <StepNavigatorScreen pressroomId={navigateToPressroomId} />;
  } else if (!pendingSource) {
    return null;
  }

  return (
    <Wrapper>
      <Content>
        {showFullPageLoader() ? (
          <SmartLoader currentStepLocaleText={currentStepLocaleText} />
        ) : (
          <>
            <TitleWrapper>
              {step === STEP.INTRO ? (
                <>
                  <IntroHeading>{t('onboarding_trial_main_title') + ' 👋'}</IntroHeading>
                  <IntroSubHeading>
                    {(currentStepLocaleText as StepsMapType['INTRO']).subTitle}
                  </IntroSubHeading>
                </>
              ) : (
                <>
                  <Text as="h1" variant="subheading" color="GREY_1000" textAlign="center">
                    {t('onboarding_trial_main_title') + ' 👋'}
                  </Text>
                  <Text as="h2" variant="headlineLarge" textAlign="center" color="GREY_1000">
                    {
                      (currentStepLocaleText as StepsMapType['BRAND'] | StepsMapType['COMPETITORS'])
                        .subTitle
                    }
                  </Text>
                </>
              )}
            </TitleWrapper>
            {/** Content */}
            {step === STEP.BRAND ? (
              <StepBrand
                originalPendingSource={pendingSource}
                setSelectedBrandIds={setSelectedBrandIds}
                trackBrandNameChange={trackBrandNameChange}
                trackBrandUrlChange={trackBrandUrlChange}
              />
            ) : step === STEP.COMPETITORS ? (
              <StepCompetitors
                originalPendingSource={pendingSource}
                selectedBrandIds={selectedBrandIds}
                setSelectedBrandIds={setSelectedBrandIds}
                trackCompetitorChange={trackCompetitorChange}
              />
            ) : null}

            {/** Disclaimer */}
            {'disclaimer' in currentStepLocaleText && currentStepLocaleText.disclaimer && (
              <Disclaimer>{currentStepLocaleText.disclaimer}</Disclaimer>
            )}
            {/** Navigation Buttons */}
            <ButtonRow $isIntro={step === STEP.INTRO}>
              {step === STEP.INTRO ? (
                <Button
                  configuration="commercial"
                  type="button"
                  onClick={() => {
                    track({
                      event: 'Onboarding intro: Navigate to Brand',
                    });
                    setStep(STEP.BRAND);
                  }}
                  trailingIcon={<IconArrowRight />}
                >
                  {t('onboarding_trial_button_cta_label_step1')}
                </Button>
              ) : (
                <>
                  <NestedButtonWrapper>
                    <Button
                      type="button"
                      configuration="commercial"
                      {...(step === STEP.COMPETITORS
                        ? { loading: creatingPressroom }
                        : { disabled: !pendingSource.brandName || !pendingSource.brandUrl })}
                      onClick={() => {
                        if (step === STEP.COMPETITORS) {
                          track({
                            event: 'Onboarding intro: Done',
                            traits: {
                              competitors: pendingSource.competitors.map(competitor => ({
                                brandName: competitor.brandName,
                                brandUrl: competitor.brandUrl,
                                manuallyAdded: trackCompetitorChange.current.has(competitor.id),
                                selected: selectedBrandIds.includes(competitor.id),
                              })),
                            },
                          });
                          setStep(STEP.COMPLETE);
                          sendCompetitorsAndSetPressroom();
                        } else if (step === STEP.BRAND) {
                          if (pendingSource.brandName && pendingSource.brandUrl) {
                            track({
                              event: 'Onboarding intro: Navigate to competitors',
                              traits: {
                                brandName: pendingSource.brandName,
                                brandUrl: pendingSource.brandUrl,
                                brandNameChangedByUser: trackBrandNameChange.current,
                                brandUrlChangedByUser: trackBrandUrlChange.current,
                              },
                            });
                            setStep(STEP.COMPETITORS);
                          }
                        }
                      }}
                      trailingIcon={<IconArrowRight />}
                    >
                      {t('common_continue')}
                    </Button>
                    {step === STEP.COMPETITORS && (
                      <Button
                        type="button"
                        configuration="text"
                        onClick={() => {
                          track({
                            event: 'Onboarding intro: Done',
                            traits: {
                              competitors: pendingSource.competitors.map(competitor => ({
                                brandName: competitor.brandName,
                                brandUrl: competitor.brandUrl,
                                manuallyAdded: false,
                                selected: false,
                              })),
                            },
                          });
                          setStep(STEP.COMPLETE);
                          // Skip competitor setting.
                          createPressroom();
                        }}
                      >
                        {t('onboarding_trial_button_skip')}
                      </Button>
                    )}
                  </NestedButtonWrapper>

                  <Button
                    type="button"
                    configuration="toned"
                    onClick={() => {
                      setStep(step === STEP.BRAND ? STEP.INTRO : STEP.BRAND);
                    }}
                    disabled={creatingPressroom}
                  >
                    {t('common_back')}
                  </Button>
                </>
              )}
            </ButtonRow>

            {/** Stepper */}
            {!pendingBrand && !pendingCompetitors && (
              <Steps $isIntro={step === STEP.INTRO}>
                <Stepper
                  numOfSteps={3}
                  activeStepIndex={
                    {
                      INTRO: 0,
                      BRAND: 1,
                      COMPETITORS: 2,
                      COMPLETE: 2, // No need a stepper, just for TS.
                    }[step]
                  }
                />
              </Steps>
            )}
          </>
        )}
      </Content>
    </Wrapper>
  );
};
// #endregion

export default Onboarding;
